import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import { 
  Globe, ArrowRight, Check, Zap, Shield, Clock,
  LayoutDashboard, FileText, Users, LogIn, UserPlus,
  RefreshCw, Image, FolderOpen, Calendar
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f50ba61d-1f02-4136-b6b8-0e3c71c5b623/artifacts/e4hujy2b_sologo-aitools-bgremover-trimmed.png";

export default function LandingPage() {
  const { t } = useTranslation();

  const features = [
    { icon: Globe, title: t('landing.features.feature1.title'), desc: t('landing.features.feature1.desc') },
    { icon: LayoutDashboard, title: t('landing.features.feature2.title'), desc: t('landing.features.feature2.desc') },
    { icon: FileText, title: t('landing.features.feature3.title'), desc: t('landing.features.feature3.desc') },
    { icon: Image, title: t('landing.features.feature4.title'), desc: t('landing.features.feature4.desc') },
    { icon: FolderOpen, title: t('landing.features.feature5.title'), desc: t('landing.features.feature5.desc') },
    { icon: Calendar, title: t('landing.features.feature6.title'), desc: t('landing.features.feature6.desc') },
  ];

  const problems = [
    { icon: LogIn, text: t('landing.problem.item1') },
    { icon: RefreshCw, text: t('landing.problem.item2') },
    { icon: LayoutDashboard, text: t('landing.problem.item3') },
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Diffuzo" className="h-8" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/pricing" className="hidden sm:block">
              <Button variant="ghost" className="text-[#1E2A5A] font-medium">
                {t('nav.pricing')}
              </Button>
            </Link>
            <LanguageSelector variant="minimal" />
            <Link to="/login">
              <Button variant="ghost" className="text-[#1E2A5A] font-medium hidden sm:flex">
                {t('auth.login.submit')}
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#1E2A5A] hover:bg-[#20C997] text-white rounded-xl px-4 sm:px-6 transition-colors">
                <span className="hidden sm:inline">{t('landing.hero.cta')}</span>
                <UserPlus className="w-4 h-4 sm:hidden" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center animate-slide-up">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold font-heading text-[#1E2A5A] leading-tight mb-6">
              {t('landing.hero.title')}
            </h1>
            <p className="text-lg sm:text-xl text-[#6B7280] mb-10 max-w-2xl mx-auto">
              {t('landing.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="bg-[#1E2A5A] hover:bg-[#20C997] text-white rounded-xl px-8 py-6 text-lg font-medium transition-all hover-lift">
                  {t('landing.hero.cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="rounded-xl px-8 py-6 text-lg font-medium border-2 border-gray-200 text-[#1E2A5A] hover:border-[#20C997] hover:text-[#20C997]">
                  {t('nav.pricing')}
                </Button>
              </Link>
            </div>
          </div>

          {/* Dashboard Preview */}
          <div className="mt-20 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#F5F7FA] z-10 pointer-events-none" style={{ top: '70%' }} />
            <div className="bg-white rounded-3xl soft-shadow overflow-hidden max-w-5xl mx-auto border border-gray-100">
              <div className="bg-[#1E2A5A] px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 text-center">
                  <span className="text-white/60 text-sm">app.diffuzo.com</span>
                </div>
              </div>
              <div className="p-6 bg-[#F5F7FA]">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 shadow-sm">
                      <div className="h-3 w-16 bg-gray-200 rounded mb-2" />
                      <div className="h-8 w-12 bg-[#1E2A5A] rounded" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 bg-white rounded-2xl p-4 shadow-sm h-48">
                    <div className="h-3 w-24 bg-gray-200 rounded mb-4" />
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-10 bg-gray-100 rounded-xl" />
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-sm h-48">
                    <div className="h-3 w-20 bg-gray-200 rounded mb-4" />
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#20C997]/20" />
                          <div className="h-3 w-20 bg-gray-100 rounded" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-[#1E2A5A] mb-4">
              {t('landing.problem.title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {problems.map((problem, idx) => {
              const Icon = problem.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-8 soft-shadow text-center hover-lift">
                  <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-red-400" strokeWidth={1.5} />
                  </div>
                  <p className="text-[#6B7280] font-medium">{problem.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-[#1E2A5A] mb-4">
              {t('landing.solution.title')}
            </h2>
            <p className="text-xl text-[#6B7280]">
              {t('landing.solution.subtitle')}
            </p>
          </div>

          {/* Hub illustration */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="relative">
              {/* Center hub */}
              <div className="w-32 h-32 bg-[#1E2A5A] rounded-full flex items-center justify-center mx-auto z-10 relative">
                <img src={LOGO_URL} alt="Diffuzo" className="h-12" />
              </div>
              {/* Orbiting sites */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-80 h-80 border-2 border-dashed border-[#20C997]/30 rounded-full animate-slow-spin">
                  {[0, 60, 120, 180, 240, 300].map((angle, idx) => (
                    <div
                      key={idx}
                      className="absolute w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center"
                      style={{
                        left: '50%',
                        top: '50%',
                        transform: `rotate(${angle}deg) translate(160px) rotate(-${angle}deg) translate(-50%, -50%)`
                      }}
                    >
                      <Globe className="w-6 h-6 text-[#20C997]" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Solution points */}
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#20C997]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-[#20C997]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-[#1E2A5A] mb-2">{t('landing.solution.item1')}</h3>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1FB6FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-[#1FB6FF]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-[#1E2A5A] mb-2">{t('landing.solution.item2')}</h3>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1E2A5A]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#1E2A5A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-[#1E2A5A] mb-2">{t('landing.solution.item3')}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-[#1E2A5A] mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-[#6B7280]">
              {t('landing.features.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-8 soft-shadow hover-lift transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-[#1E2A5A]/5 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#1E2A5A]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-[#1E2A5A] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-[#6B7280]">
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#1E2A5A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mb-4">
            {t('landing.cta.title')}
          </h2>
          <p className="text-lg text-white/70 mb-8">
            {t('landing.cta.subtitle')}
          </p>
          <Link to="/register">
            <Button className="bg-[#20C997] hover:bg-white hover:text-[#1E2A5A] text-white rounded-xl px-8 py-6 text-lg font-medium transition-all">
              {t('landing.cta.button')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Diffuzo" className="h-8" />
              <span className="text-[#6B7280]">{t('landing.footer.slogan')}</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-[#6B7280] hover:text-[#1E2A5A] transition-colors">
                {t('nav.pricing')}
              </Link>
              <Link to="/login" className="text-[#6B7280] hover:text-[#1E2A5A] transition-colors">
                {t('auth.login.submit')}
              </Link>
              <LanguageSelector />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-100 text-center text-sm text-[#6B7280]">
            © {new Date().getFullYear()} Diffuzo. {t('landing.footer.rights')}
          </div>
        </div>
      </footer>
    </div>
  );
}
